import clsx from 'clsx'
import { ArrowLeftIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { TextLink, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { convertCentsToGrapeMoney, convertMoneyToGrapeMoney } from 'utils'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment } from 'utils/web_api'
import {
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier,
  type PostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as EnrollmentData
} from 'utils/web_api.schemas'

import { PageErrorBanner } from '../components/PageErrorBanner'
import { SmartBudgetAllowanceCard } from '../components/SmartBudget/AllowanceCard/AllowanceCard'
import { SmartBudgetAncillaryPlan } from '../components/SmartBudget/AncillaryPlan'
import { SmartBudgetMedicalPlan } from '../components/SmartBudget/MedicalPlan'
import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import {
  STRATEGY_CONFIGS,
  STRATEGY_ORDER,
} from '../smartBudgetUtils'
import {
  type BenefitsBundleStrategy,
  type CoverageType,
  type DentalPlan,
  type StepComponentProps,
  type VisionPlan,
} from '../types'

const baseButtonStyles = 'flex flex-1 p-2 items-center justify-center rounded-xl transition focus:outline-none focus-visible:outline-thatch-400 focus-visible:outline-offset-[-2px]'
const activeStyles = 'bg-thatch-100 border border-thatch-500'
const inactiveStyles = 'bg-white hover:bg-gray-100 focus-visible:bg-gray-100'

const ZERO_DOLLAR_ALLOWANCE: GrapeMoney = {
  subunits: 0,
  currency: 'USD',
  formatted_amount: '$0.00'
}

// TODO(@jason): Feels like this should live somewhere else
const VALID_METAL_TIERS = new Set([
  'catastrophic',
  'bronze',
  'expanded_bronze',
  'silver',
  'gold',
  'platinum'
] as const)

function getMinimumMetalTier (metadata?: Record<string, any>): MetalTier {
  const metadataValue = metadata?.minimumMetalTier

  if (typeof metadataValue === 'string' && VALID_METAL_TIERS.has(metadataValue as MetalTier)) {
    return metadataValue as MetalTier
  }

  return 'bronze'
}

// Strategy Button Component
interface StrategyButtonProps {
  strategy: BenefitsBundleStrategy
  label: string
  currentStrategy: BenefitsBundleStrategy
  onClick: (strategy: BenefitsBundleStrategy) => void
}

const StrategyButton = ({ strategy, label, currentStrategy, onClick }: StrategyButtonProps) => (
  <button
    type='button'
    onClick={() => {
      window.segment?.track(
        '[EE] Smart budget strategy clicked',
        { strategy }
      )
      onClick(strategy)
    }}
    className={clsx(baseButtonStyles, {
      [activeStyles]: strategy === currentStrategy,
      [inactiveStyles]: strategy !== currentStrategy
    })}
  >
    <div className='text-main body-bold'>{label}</div>
  </button>
)

const medicalEnrollmentData = (plan: MedicalPlanDisplayItem): EnrollmentData => {
  return {
    covers_medical: true,
    covers_dental: false,
    covers_vision: false,
    plan_code: plan.plan.id,
    carrier_name: plan.plan.carrier_name,
    quoted_premium: (plan.total_premium?.subunits || 0) / 100,
    employee_only_premium:
      (plan.tiered_allowance_employee_only_premium?.subunits || 0) / 100,
  }
}

const ancillaryEnrollmentData = (plan: DentalPlan | VisionPlan, coverageType: 'dental' | 'vision'): EnrollmentData => {
  return {
    plan_code: plan.plan_code,
    carrier_name: plan.carrier,
    state: 'in_member_cart',
    covers_medical: false,
    covers_dental: coverageType === 'dental',
    covers_vision: coverageType === 'vision',
    quoted_premium: Number(plan.premium.subunits / 100),
  }
}

export function SmartBudgetPage ({
  navigateToNextStep,
  navigateToPreviousStep,
  metadata,
}: StepComponentProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const [continueError, setContinueError] = useState<GrapeAPIErrorType | null>(null)

  const handleBack = () => {
    navigateToPreviousStep?.()
  }

  const { onboardingData, enrollmentEventId } = useOnboarding()

  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date

  // Load existing enrollments in the member cart to prefill the dental & vision plans
  const enrollmentItems = onboardingData?.enrollments || []

  // TODO(@jason): Save this in PlanSelectionMetadata
  const [budgetStrategy, setBudgetStrategy] = useState<BenefitsBundleStrategy>('flexible')

  const [medicalPlan, setMedicalPlan] = useState<MedicalPlanDisplayItem | null>(null)
  const [dentalPlan, setDentalPlan] = useState<DentalPlan | null>(null)
  const [visionPlan, setVisionPlan] = useState<VisionPlan | null>(null)

  const [viewingCoverageType, setViewingCoverageType] = useState<CoverageType | null>(null)

  const createEnrollment = useCreateEnrollment()

  const createEnrollmentForPlan = (data: EnrollmentData) => {
    return createEnrollment.mutateAsync({
      enrollmentEventId,
      data,
    })
  }

  const handleContinueClick = async () => {
    window.segment?.track('[EE] Smart budget continue clicked', {
      strategy: budgetStrategy,
      medical_plan_id: medicalPlan?.plan.id,
      medical_plan_name: medicalPlan?.plan.display_name,
      medical_plan_carrier: medicalPlan?.plan.carrier_name,
      dental_plan_id: dentalPlan?.plan_code,
      dental_plan_name: dentalPlan?.name,
      dental_plan_carrier: dentalPlan?.carrier,
      vision_plan_id: visionPlan?.plan_code,
      vision_plan_name: visionPlan?.name,
      vision_plan_carrier: visionPlan?.carrier,
      medical_plan_premium: medicalPlan?.total_premium?.formatted_amount,
      dental_plan_premium: dentalPlan?.premium.formatted_amount,
      vision_plan_premium: visionPlan?.premium.formatted_amount,
      total_premium: totalMonthlyPremiums.formatted_amount,
      remaining_balance: remainingBalance.formatted_amount,
      qme_enabled: qmeEnabled,
      soonest_coverage_start_date: soonestCoverageStartDate,
    })

    setContinueError(null)

    try {
      const enrollmentPromises: Promise<any>[] = []

      if (medicalPlan) {
        enrollmentPromises.push(
          createEnrollmentForPlan(medicalEnrollmentData(medicalPlan))
        )
      }

      // Conditionally create dental enrollment if enabled and selected
      if (dentalPlansEnabled && dentalPlan) {
        enrollmentPromises.push(
          createEnrollmentForPlan(ancillaryEnrollmentData(dentalPlan, 'dental'))
        )
      }

      // Conditionally create vision enrollment if enabled and selected
      if (visionPlansEnabled && visionPlan) {
        enrollmentPromises.push(
          createEnrollmentForPlan(ancillaryEnrollmentData(visionPlan, 'vision'))
        )
      }

      // Wait for all enrollments to complete
      await Promise.all(enrollmentPromises)

      // Navigate to next step only after all enrollments are successful
      navigateToNextStep?.()
    } catch (error) {
      setContinueError(error as GrapeAPIErrorType)
    }
  }

  // TODO(@jason): Handle tiered allowances
  const allowanceAmount: GrapeMoney = onboardingData?.allowance?.allowance_amount || ZERO_DOLLAR_ALLOWANCE

  const isIndividual =
    (onboardingData?.additional_participants || []).length === 0

  const qmeEnabled = onboardingData?.allowance?.qme_treatment === 'all_allowed'

  const dentalPlanCodeInMemberCart = enrollmentItems.find((e) => e.covers_dental)?.plan_code || null
  const visionPlanCodeInMemberCart = enrollmentItems.find((e) => e.covers_vision)?.plan_code || null

  const dentalPlanCannotBeChanged =
    onboardingData?.restrictions?.dental_not_available === 'coverage_ends_mid_year'
  const visionPlanCannotBeChanged =
    onboardingData?.restrictions?.vision_not_available === 'coverage_ends_mid_year'

  // We want to show the dental and vision plans for renewals purposes, even if they are not able to be changed
  const dentalPlansEnabled = (dentalPlanCannotBeChanged && dentalPlanCodeInMemberCart) || !onboardingData?.restrictions?.dental_not_available
  const visionPlansEnabled = (visionPlanCannotBeChanged && visionPlanCodeInMemberCart) || !onboardingData?.restrictions?.vision_not_available

  // TODO(@jason): Not sure how to pass from MedicalPlansPage to SmartBudgetPage
  // May use metadata or sessionStorage
  const minimumMetalTier = getMinimumMetalTier(metadata)

  const medicalPlanPremium: GrapeMoney = convertMoneyToGrapeMoney(medicalPlan?.total_premium)

  const { totalMonthlyPremiums, remainingBalance } = useMemo(() => {
    const totalPremiumSubunits =
      medicalPlanPremium.subunits +
      (dentalPlan?.premium.subunits || 0) +
      (visionPlan?.premium.subunits || 0)

    const remainingSubunits = allowanceAmount.subunits - totalPremiumSubunits

    return {
      totalMonthlyPremiums: convertCentsToGrapeMoney(totalPremiumSubunits),
      remainingBalance: convertCentsToGrapeMoney(remainingSubunits),
    }
  }, [allowanceAmount, medicalPlanPremium, dentalPlan, visionPlan])

  const remainingAllowance = remainingBalance.subunits / 100
  const sideBySideLayout = breakpointIsAtLeast('xl')

  return (
    <>
      <TextLink onClick={handleBack} leftIcon={ArrowLeftIcon}>
        <span className='cursor-pointer'>Back to all plans</span>
      </TextLink>

      <div className='my-6'>
        <h1 className='text-main heading-lg mb-3'>
          Smart Budget
        </h1>
        <p className='body text-gray-500'>
          Guided recommendations that help you make the most of your health allowance.
        </p>
      </div>

      {continueError && (
        <div className='mt-6'>
          <PageErrorBanner error={continueError} />
        </div>
      )}

      <div className={clsx('flex flex-row gap-6', sideBySideLayout ? 'flex-row' : 'flex-col')}>
        <div className='flex flex-col gap-4'>
          <div className='p-6 border border-gray-200 bg-gray-50 rounded-2xl max-w-[500px]'>
            <div className='p-1 border border-gray-200 bg-white rounded-xl'>
              <div className='flex flex-row gap-1'>
                {STRATEGY_ORDER.map((strategyKey) => (
                  <StrategyButton
                    key={strategyKey}
                    strategy={strategyKey}
                    label={STRATEGY_CONFIGS[strategyKey].label}
                    currentStrategy={budgetStrategy}
                    onClick={setBudgetStrategy}
                  />
                ))}
              </div>
            </div>
            <div className='body text-gray-500 mt-4 mb-2'>
              {STRATEGY_CONFIGS[budgetStrategy].description}
            </div>
            <SmartBudgetMedicalPlan
              enrollmentEventId={enrollmentEventId}
              viewingCoverageType={viewingCoverageType}
              setViewingCoverageType={setViewingCoverageType}
              remainingAllowance={remainingAllowance}
              onSelectPlan={setMedicalPlan}
              isIndividual={isIndividual}
              allowanceAmount={allowanceAmount}
              minimumMetalTier={minimumMetalTier}
              budgetStrategy={budgetStrategy}
            />
            {dentalPlansEnabled &&
              <SmartBudgetAncillaryPlan
                enrollmentEventId={enrollmentEventId}
                coverageType='dental'
                viewingCoverageType={viewingCoverageType}
                setViewingCoverageType={setViewingCoverageType}
                remainingAllowance={remainingAllowance}
                selectedPlan={dentalPlan}
                onSelectPlan={(plan) => setDentalPlan(plan as DentalPlan | null)}
                planCodeInMemberCart={dentalPlanCodeInMemberCart}
                planCannotBeChanged={dentalPlanCannotBeChanged}
              />}
            {visionPlansEnabled &&
              <SmartBudgetAncillaryPlan
                enrollmentEventId={enrollmentEventId}
                coverageType='vision'
                viewingCoverageType={viewingCoverageType}
                setViewingCoverageType={setViewingCoverageType}
                remainingAllowance={remainingAllowance}
                selectedPlan={visionPlan}
                onSelectPlan={(plan) => setVisionPlan(plan as VisionPlan | null)}
                planCodeInMemberCart={visionPlanCodeInMemberCart}
                planCannotBeChanged={visionPlanCannotBeChanged}
              />}
          </div>
        </div>
        <div className={clsx('w-[370px]', {
          'w-full max-w-[500px]': !sideBySideLayout
        })}
        >
          <SmartBudgetAllowanceCard
            monthlyAllowance={allowanceAmount}
            totalMonthlyPremiums={totalMonthlyPremiums}
            qmeEnabled={qmeEnabled}
            soonestCoverageStartDate={soonestCoverageStartDate}
            onContinueClick={handleContinueClick}
          />
        </div>
      </div>
    </>
  )
}
